import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Calendar, Clock, User, Radio } from "lucide-react";
import api from "@/lib/api";
import { TopNav } from "@/components/TopNav";
import { StatusBadge, LiveBadge } from "@/components/LiveBadge";
import { fmtDateTime } from "@/lib/time";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1762968274962-20c12e6e8ecd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBwcmVzZW50YXRpb24lMjBzdGFnZXxlbnwwfHx8fDE3ODI4NDAxMDh8MA&ixlib=rb-4.1.0&q=85";

export default function EmployeeDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState({ live: [], upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/webinars").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A]">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="overline mb-2">Broadcast Schedule</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-10">Webinars</h1>

        {loading ? (
          <p className="text-gray-500">Loading webinars…</p>
        ) : (
          <div className="space-y-14">
            {/* LIVE NOW */}
            <section>
              <div className="flex items-center gap-3 mb-5">
                <h2 className="font-display font-bold text-lg uppercase tracking-wide">Live Now</h2>
                {data.live.length > 0 && <LiveBadge />}
              </div>
              {data.live.length === 0 ? (
                <div className="border border-dashed border-[#E4E4E7] rounded-sm p-8 text-center text-gray-600">
                  <Radio className="mx-auto mb-3 text-gray-700" size={28} />
                  No webinars are live right now.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {data.live.map((w) => (
                    <div
                      key={w.id}
                      data-testid={`live-webinar-${w.id}`}
                      className="card-hover bg-[#F5F5F5] border border-[#D92D20]/30 rounded-sm overflow-hidden cursor-pointer"
                      onClick={() => nav(`/webinars/${w.id}`)}
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img src={w.banner_image || FALLBACK_IMG} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                        <div className="absolute top-3 left-3"><LiveBadge /></div>
                        <button
                          data-testid={`watch-btn-${w.id}`}
                          className="absolute bottom-3 right-3 flex items-center gap-2 bg-[#0090A8] text-black font-bold rounded-sm px-5 py-2 hover:bg-[#00728A] transition-colors"
                        >
                          <Play size={16} fill="black" /> Watch
                        </button>
                      </div>
                      <div className="p-6">
                        <h3 className="font-display font-bold text-xl tracking-tight mb-1">{w.title}</h3>
                        <p className="text-gray-400 text-sm line-clamp-2 mb-3">{w.description}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500"><User size={14} /> {w.presenter_name || "TBA"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* UPCOMING */}
            <section>
              <h2 className="font-display font-bold text-lg uppercase tracking-wide mb-5">Upcoming</h2>
              {data.upcoming.length === 0 ? (
                <p className="text-gray-600">No upcoming webinars scheduled.</p>
              ) : (
                <div className="grid md:grid-cols-3 gap-6">
                  {data.upcoming.map((w) => (
                    <div key={w.id} data-testid={`upcoming-webinar-${w.id}`} className="card-hover bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm overflow-hidden">
                      <div className="aspect-video overflow-hidden">
                        <img src={w.banner_image || FALLBACK_IMG} alt="" className="w-full h-full object-cover opacity-80" />
                      </div>
                      <div className="p-5">
                        <StatusBadge status="upcoming" />
                        <h3 className="font-display font-bold text-lg tracking-tight mt-3 mb-2">{w.title}</h3>
                        <p className="text-gray-400 text-sm line-clamp-2 mb-4">{w.description}</p>
                        <div className="space-y-1.5 text-sm text-gray-500">
                          <div className="flex items-center gap-2"><User size={14} /> {w.presenter_name || "TBA"}</div>
                          <div className="flex items-center gap-2"><Calendar size={14} /> {fmtDateTime(w.start_datetime)}</div>
                          <div className="flex items-center gap-2"><Clock size={14} /> {w.duration_minutes} min</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* PAST */}
            <section>
              <h2 className="font-display font-bold text-lg uppercase tracking-wide mb-5">Past Webinars</h2>
              {data.past.length === 0 ? (
                <p className="text-gray-600">No past webinars yet.</p>
              ) : (
                <div className="border border-[#E4E4E7] rounded-sm divide-y divide-[#E4E4E7]">
                  {data.past.map((w) => (
                    <div key={w.id} data-testid={`past-webinar-${w.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-black/5 transition-colors">
                      <div>
                        <div className="font-medium">{w.title}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{fmtDateTime(w.start_datetime)} · {w.presenter_name}</div>
                      </div>
                      <StatusBadge status="ended" />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
